import React, { useState, useEffect } from 'react';
import { Card, Typography, Space, Tag, Spin } from '@goalsetter/ant-design-ui';
import { useBrandingContext } from '@/contexts';
import { BrandingImage, BrandingImageType } from '../BrandingImage';
import { styles } from './styles';

const { Title, Text } = Typography;

export const BrandingDebug: React.FC = () => {
  const [isVisible, setIsVisible] = useState(false);
  const {
    subdomain,
    branding,
    config,
    isLoading,
    error,
    isLoaded,
    getClientName,
    getClientCode,
    getBrandingLogo,
    getStylesVersion,
    hasBranding,
    hasConfig,
  } = useBrandingContext();

  useEffect(() => {
    if (!import.meta.env.DEV) {
      return;
    }

    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.shiftKey && event.key.toLowerCase() === 'd') {
        setIsVisible((prev) => !prev);
      }
    };

    document.addEventListener('keydown', handleKeyDown);

    return () => {
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, []);

  if (!import.meta.env.DEV) {
    return null;
  }

  if (!isVisible) {
    return null;
  }

  if (isLoading) {
    return (
      <Card style={styles.loadingCard}>
        <Space
          direction="vertical"
          align="center"
        >
          <Spin size="large" />
          <Text>Loading branding configuration...</Text>
        </Space>
      </Card>
    );
  }

  if (error) {
    return (
      <Card style={styles.errorCard}>
        <Space direction="vertical">
          <Title level={4}>Error</Title>
          <Text type="danger">{error}</Text>
        </Space>
      </Card>
    );
  }

  return (
    <Card style={styles.mainCard}>
      <Space
        direction="vertical"
        size="large"
        style={styles.mainSpace}
      >
        <Title level={4}>Branding Configuration Debug</Title>

        <div>
          <Title level={5}>Subdomain Information</Title>
          <Space
            direction="vertical"
            size="small"
          >
            <div>
              <Text strong>Subdomain:</Text>{' '}
              {subdomain ? (
                <Tag color="blue">{subdomain}</Tag>
              ) : (
                <Tag color="default">None</Tag>
              )}
            </div>
            <div>
              <Text strong>Is Loaded:</Text>{' '}
              <Tag color={isLoaded ? 'green' : 'orange'}>
                {isLoaded ? 'Yes' : 'No'}
              </Tag>
            </div>
          </Space>
        </div>

        <div>
          <Title level={5}>Client Information</Title>
          <Space
            direction="vertical"
            size="small"
          >
            <div>
              <Text strong>Client Name:</Text>{' '}
              <Text code>{getClientName() || 'Not available'}</Text>
            </div>
            <div>
              <Text strong>Client Code:</Text>{' '}
              <Text code>{getClientCode() || 'Not available'}</Text>
            </div>
            <div>
              <Text strong>Has Config:</Text>{' '}
              <Tag color={hasConfig ? 'green' : 'red'}>
                {hasConfig ? 'Yes' : 'No'}
              </Tag>
            </div>
            <div>
              <Text strong>Has Branding:</Text>{' '}
              <Tag color={hasBranding ? 'green' : 'red'}>
                {hasBranding ? 'Yes' : 'No'}
              </Tag>
            </div>
          </Space>
        </div>

        {hasBranding && (
          <div>
            <Title level={5}>Branding Details</Title>
            <Space
              direction="vertical"
              size="small"
            >
              <div>
                <Text strong>Styles Version:</Text>{' '}
                <Text code>{getStylesVersion() || 'Not available'}</Text>
              </div>
              <div>
                <Text strong>Logo URL:</Text>{' '}
                <Text code>{getBrandingLogo() || 'Not available'}</Text>
                <div style={styles.logoContainer}>
                  <BrandingImage
                    type={BrandingImageType.LOGO}
                    alt="Logo"
                    height={40}
                  />
                  <BrandingImage
                    type={BrandingImageType.ISO}
                    alt="Logo"
                    height={40}
                  />
                </div>
              </div>
              {branding?.branding?.icons && (
                <div>
                  <Text strong>Available Icons:</Text>
                  <Space wrap>
                    {Object.keys(branding.branding.icons).map((iconType) => (
                      <Tag
                        key={iconType}
                        color="purple"
                      >
                        {iconType}
                      </Tag>
                    ))}
                  </Space>
                </div>
              )}
            </Space>
          </div>
        )}

        {hasConfig && config && (
          <div>
            <Title level={5}>Config Details</Title>
            <Space
              direction="vertical"
              size="small"
            >
              <div>
                <Text strong>Config Name:</Text> <Text code>{config.name}</Text>
              </div>
              <div>
                <Text strong>Config Code:</Text> <Text code>{config.code}</Text>
              </div>
            </Space>
          </div>
        )}
      </Space>
    </Card>
  );
};
